import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MailCheck, Send } from "lucide-react";
import { fmtINR } from "./bizdevUtils";

/** Iter 170 · Founder Brief settings + preview + send-now. */

export default function FounderBriefDialog({ wsId, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/bizdev/settings`)
      .then((r) => setCfg({ ...r.data, custom_emails_text: (r.data.brief_custom_emails || []).join(", ") }))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load settings")));
    api.get(`/workspaces/${wsId}/bizdev/founder-brief/preview`)
      .then((r) => setPreview(r.data)).catch(() => {});
  }, [wsId]);

  const payload = () => ({
    founder_brief_enabled: !!cfg.founder_brief_enabled,
    brief_recipients_mode: cfg.brief_recipients_mode || "owners",
    brief_custom_emails: (cfg.custom_emails_text || "").split(/[,;\n]/)
      .map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/workspaces/${wsId}/bizdev/settings`, payload());
      setCfg({ ...data, custom_emails_text: (data.brief_custom_emails || []).join(", ") });
      toast.success(data.founder_brief_enabled
        ? "Founder Brief ON — every Monday 07:15 UTC" : "Founder Brief paused");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.put(`/workspaces/${wsId}/bizdev/settings`, payload());
      const { data } = await api.post(`/workspaces/${wsId}/bizdev/founder-brief/send-now`);
      toast.success(data.overall === "sent"
        ? `Brief emailed to ${data.recipients.join(", ")}`
        : `Brief queued (${data.overall}) → ${data.recipients.join(", ")}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="founder-brief-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailCheck className="w-4 h-4" /> Monday Founder Brief
          </DialogTitle>
          <DialogDescription>
            Top-3 RICE ideas, overdue next-actions, and how your weighted upside moved — in your inbox every Monday.
          </DialogDescription>
        </DialogHeader>

        {!cfg ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between border border-border p-3">
              <div className="font-medium">Send every Monday (07:15 UTC)</div>
              <Switch checked={!!cfg.founder_brief_enabled}
                      onCheckedChange={(v) => setCfg({ ...cfg, founder_brief_enabled: v })}
                      data-testid="brief-enabled-switch" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Recipients</label>
                <Select value={cfg.brief_recipients_mode || "owners"}
                        onValueChange={(v) => setCfg({ ...cfg, brief_recipients_mode: v })}>
                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="brief-recipients-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owners">Workspace owners & admins</SelectItem>
                    <SelectItem value="custom">Custom list</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cfg.brief_recipients_mode === "custom" && (
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Emails</label>
                  <Input value={cfg.custom_emails_text || ""}
                         onChange={(e) => setCfg({ ...cfg, custom_emails_text: e.target.value })}
                         placeholder="me@company.com"
                         className="h-8 text-xs mt-0.5" data-testid="brief-custom-emails-input" />
                </div>
              )}
            </div>

            {preview && (
              <div className="border border-border p-3 space-y-2" data-testid="brief-preview">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  This week's brief would say
                </div>
                <div className="text-xs">
                  Pipeline: <b>{fmtINR(preview.upside_monthly)}/mo</b> + <b>{fmtINR(preview.upside_one_time)}</b> one-time
                  {preview.delta_monthly != null && (
                    <span className={preview.delta_monthly >= 0 ? "text-emerald-600" : "text-red-600"}>
                      {" "}({preview.delta_monthly >= 0 ? "▲" : "▼"} {fmtINR(Math.abs(preview.delta_monthly))}/mo vs last week)
                    </span>
                  )}
                </div>
                <div className="text-xs">
                  <b>Top ideas:</b> {preview.top_ideas.map((i) => i.title).join(" · ") || "none yet"}
                </div>
                <div className="text-xs">
                  <b>Overdue actions:</b>{" "}
                  {preview.overdue_actions.length === 0
                    ? <span className="text-emerald-600">none — clean slate ✓</span>
                    : <span className="text-red-600">{preview.overdue_actions.length} overdue</span>}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={sendNow}
                  disabled={!cfg || sending} data-testid="brief-send-now-btn">
            <Send className="w-3 h-3 mr-1" /> {sending ? "Sending…" : "Send now"}
          </Button>
          <Button size="sm" onClick={save} disabled={!cfg || saving}
                  data-testid="brief-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
